import { useState, useRef, useEffect } from "react";
import { X, Loader2, CheckCircle2, CalendarClock } from "lucide-react";
import { Turnstile } from "@marsidev/react-turnstile";
import axios from "axios";
import { EMAIL_REGEX } from "@/lib/validators";
import { resolveApiError } from "@/lib/api-errors";

const API_URL = process.env.REACT_APP_API_URL || "";
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
const CAL_URL = "https://cal.com/ekklesiaonline-demo";

const FIELD =
  "mt-1.5 w-full rounded-xl border border-violet-light bg-white px-4 py-3 text-[15px] text-ink shadow-sm outline-none transition-all duration-150 focus:border-violet-fuerte focus:ring-2 focus:ring-violet-fuerte/20 placeholder:text-ink-3";
const LABEL = "block text-sm font-semibold text-ink";
const ERROR = "mt-1 text-[13px] text-red-500";

// Etiqueta visible por plan.
const PLAN_LABEL = {
  esencial: "Esencial",
  avanzado: "Avanzado",
  institucional: "Institucional",
};

// Mensaje prellenado por plan (editable por quien lo envía).
const PLAN_MESSAGE = {
  esencial:
    "Hola, estoy interesado/a en el plan Esencial de EkklesiaOnline (39€/mes). Me gustaría recibir más información y saber cómo empezar.",
  avanzado:
    "Hola, estoy interesado/a en el plan Avanzado de EkklesiaOnline (69€/mes). Me gustaría recibir más información y saber cómo empezar.",
  institucional:
    "Hola, estoy interesado/a en el plan Institucional de EkklesiaOnline (desde 139€/mes). Somos una red de iglesias y nos gustaría conocer las opciones disponibles.",
};

const RequiredMark = () => <span className="text-red-500">*</span>;

export default function ContactPlanModal({ isOpen, onClose, plan }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [noPhone, setNoPhone] = useState(false);
  const [errors, setErrors] = useState({});
  const [turnstileToken, setTurnstileToken] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const [sentName, setSentName] = useState("");
  const overlayRef = useRef(null);

  const planLabel = plan ? PLAN_LABEL[plan] : "";

  // Cierre con Escape.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // Lock scroll del body mientras el modal está abierto.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // Reset y prellenado del mensaje al abrir o cambiar de plan.
  useEffect(() => {
    if (isOpen && plan) {
      setForm({ name: "", email: "", phone: "", message: PLAN_MESSAGE[plan] || "" });
      setNoPhone(false);
      setErrors({});
      setTurnstileToken("");
      setStatus("idle");
      setErrorMsg("");
      setSentName("");
    }
  }, [isOpen, plan]);

  if (!isOpen || !plan) return null;

  function validate() {
    const e = {};
    if (!form.name.trim()) e.name = "El nombre es obligatorio.";
    if (!form.email.trim()) e.email = "El correo es obligatorio.";
    else if (!EMAIL_REGEX.test(form.email)) e.email = "Correo no válido.";
    if (!noPhone && !form.phone.trim())
      e.phone = "Introduce tu teléfono o marca que prefieres no compartirlo";
    if (!form.message.trim()) e.message = "El mensaje es obligatorio.";
    if (TURNSTILE_SITE_KEY && !turnstileToken) e.turnstile = "Completa la verificación.";
    return e;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    setStatus("loading");
    const phoneValue = noPhone ? "Prefiero no compartirlo" : form.phone.trim() || "No indicado";
    const fullMessage =
      `${form.message.trim()}\n\n` +
      `Plan de interés: ${planLabel}\n` +
      `Teléfono: ${phoneValue}`;

    try {
      // Reutiliza el sistema de contacto existente (/public/contact). El plan
      // viaja en "intent" (se muestra como Motivo en la notificación interna)
      // y el teléfono se adjunta al mensaje, ya que el endpoint no tiene un
      // campo de teléfono propio.
      await axios.post(`${API_URL}/public/contact`, {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        churchName: "No indicada",
        intent: `Consulta plan ${planLabel}`,
        message: fullMessage,
        turnstileToken: turnstileToken || undefined,
      });
      setSentName(form.name.trim());
      setStatus("success");
    } catch (err) {
      if (err?.response?.data?.code === "TURNSTILE_FAILED") {
        setTurnstileToken("");
      }
      setErrorMsg(
        resolveApiError(err, {
          RATE_LIMITED: "Demasiadas solicitudes. Inténtalo más tarde.",
          TURNSTILE_FAILED: "La verificación falló. Inténtalo de nuevo.",
        })
      );
      setStatus("error");
    }
  }

  function handleScheduleCall() {
    window.open(CAL_URL, "_blank", "noopener,noreferrer");
    onClose();
  }

  return (
    <div
      ref={overlayRef}
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
      className="fixed inset-0 z-[200] flex items-stretch justify-center md:items-center md:px-4"
      style={{ background: "rgba(26,17,53,0.45)", backdropFilter: "blur(4px)" }}
      role="dialog"
      aria-modal="true"
      aria-label={`Más información sobre el plan ${planLabel}`}
    >
      <div className="relative flex w-full max-h-screen flex-col overflow-y-auto rounded-none bg-white shadow-[0_32px_80px_-16px_rgba(26,17,53,0.3)] md:h-auto md:max-h-[90vh] md:max-w-lg md:rounded-3xl lg:max-w-2xl">
        {/* Top bar */}
        <div
          className="flex items-start justify-between border-b border-violet-light px-6 py-5 md:px-8"
          style={{ background: "linear-gradient(135deg, #EEEBFF 0%, #F5F3FF 100%)" }}
        >
          <div>
            <p className="font-display text-base font-bold leading-tight text-ink md:text-lg">
              Más información sobre el plan {planLabel}
            </p>
            <p className="mt-1 text-[13px] text-ink-2">
              Cuéntanos qué necesitas y te respondemos en menos de 24 horas.
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-4 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-violet-light bg-white text-ink-3 transition-colors hover:text-ink"
            aria-label="Cerrar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6 md:px-8 md:py-8">
          {status === "success" ? (
            <div className="flex flex-col items-center gap-5 py-4 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#EDF8F3]">
                <CheckCircle2 className="h-8 w-8 text-[#2D8A5E]" />
              </div>
              <div>
                <p className="font-display text-lg font-bold text-ink">
                  ¡Gracias {sentName}!
                </p>
                <p className="mt-2 text-[15px] text-ink-2 leading-relaxed">
                  Hemos recibido tu consulta y te contactaremos pronto.
                </p>
              </div>

              <div className="w-full rounded-2xl border border-violet-light bg-[#F5F3FF] px-5 py-4">
                <p className="text-[15px] font-semibold text-ink">
                  ¿Deseas agendar una llamada con nuestro equipo ahora mismo?
                </p>
                <div className="mt-4 flex flex-col gap-2.5">
                  <button
                    onClick={handleScheduleCall}
                    className="flex h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-violet-fuerte text-[15px] font-semibold text-white shadow-[0_6px_24px_-6px_rgba(74,58,138,0.45)] transition-colors hover:bg-violet-oscuro"
                  >
                    <CalendarClock className="h-[18px] w-[18px]" />
                    Sí, agendar llamada
                  </button>
                  <button
                    onClick={onClose}
                    className="flex h-[44px] w-full items-center justify-center rounded-xl border border-violet-light bg-white text-[15px] font-semibold text-ink-2 transition-colors hover:text-ink"
                  >
                    No, gracias
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label htmlFor="plan-name" className={LABEL}>
                    Nombre <RequiredMark />
                  </label>
                  <input
                    id="plan-name"
                    type="text"
                    placeholder="Tu nombre"
                    value={form.name}
                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                    className={FIELD}
                    autoComplete="name"
                    maxLength={200}
                  />
                  {errors.name && <p className={ERROR}>{errors.name}</p>}
                </div>

                <div>
                  <label htmlFor="plan-email" className={LABEL}>
                    Correo electrónico <RequiredMark />
                  </label>
                  <input
                    id="plan-email"
                    type="email"
                    placeholder="tu@correo.com"
                    value={form.email}
                    onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                    className={FIELD}
                    autoComplete="email"
                    maxLength={200}
                  />
                  {errors.email && <p className={ERROR}>{errors.email}</p>}
                </div>
              </div>

              <div>
                <label htmlFor="plan-phone" className={LABEL}>
                  Teléfono <RequiredMark />
                </label>
                <input
                  id="plan-phone"
                  type="tel"
                  placeholder="Tu número de teléfono"
                  value={noPhone ? "" : form.phone}
                  onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                  className={`${FIELD} ${noPhone ? "cursor-not-allowed bg-violet-light/30 opacity-50" : ""}`}
                  autoComplete="tel"
                  disabled={noPhone}
                />
                {errors.phone && <p className={ERROR}>{errors.phone}</p>}
                <label className="mt-2 flex cursor-pointer select-none items-center gap-2 text-[13px] text-ink-2">
                  <input
                    type="checkbox"
                    checked={noPhone}
                    onChange={(e) => {
                      setNoPhone(e.target.checked);
                      if (e.target.checked) setErrors((prev) => ({ ...prev, phone: undefined }));
                    }}
                    className="h-4 w-4 rounded border-violet-light text-violet-fuerte focus:ring-violet-fuerte/30"
                  />
                  Prefiero no compartirlo
                </label>
              </div>

              <div>
                <label htmlFor="plan-message" className={LABEL}>
                  Mensaje <RequiredMark />
                </label>
                <textarea
                  id="plan-message"
                  rows={4}
                  placeholder="Cuéntanos qué necesitas"
                  value={form.message}
                  onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
                  className={`${FIELD} resize-none`}
                  maxLength={5000}
                />
                {errors.message && <p className={ERROR}>{errors.message}</p>}
              </div>

              {TURNSTILE_SITE_KEY && (
                <div>
                  <Turnstile
                    siteKey={TURNSTILE_SITE_KEY}
                    onSuccess={(token) => setTurnstileToken(token)}
                    onExpire={() => setTurnstileToken("")}
                    onError={() => setTurnstileToken("")}
                    options={{ theme: "light", language: "es" }}
                  />
                  {errors.turnstile && <p className={ERROR}>{errors.turnstile}</p>}
                </div>
              )}

              {status === "error" && (
                <p className="rounded-xl bg-red-50 px-4 py-3 text-[13px] text-red-600">{errorMsg}</p>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                className="flex h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-violet-fuerte text-[17px] font-semibold text-white shadow-[0_6px_24px_-6px_rgba(74,58,138,0.45)] transition-all duration-300 hover:bg-violet-oscuro disabled:opacity-60"
              >
                {status === "loading" ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" /> Enviando...
                  </>
                ) : (
                  "Enviar consulta"
                )}
              </button>

              <p className="text-center text-[12px] text-ink-3">
                Sin compromiso · Respondemos en menos de 24 h
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
